import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Loader2,
  ArrowLeft,
  ShieldCheck,
  Truck,
  RefreshCw,
  MessageCircle,
  Minus,
  Plus,
  Star,
  CheckCircle2,
  Heart,
  Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  fetchProductByHandle,
  fetchRelatedProducts,
  formatPrice,
  calcDiscountPercent,
  type ShopifyProduct,
} from "@/lib/shopify";
import { ProductCard } from "@/components/shop/ProductCard";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";

type Product = ShopifyProduct["node"];
const WHATSAPP_NUMBER = "923707851120";

export const Route = createFileRoute("/product/$handle")({
  component: ProductPage,
});

function ProductPage() {
  const { handle } = Route.useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [related, setRelated] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [variantIndex, setVariantIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [wishlisted, setWishlisted] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);

  useEffect(() => {
    setLoading(true);
    setImageIndex(0);
    setQuantity(1);
    fetchProductByHandle(handle)
      .then(async (p) => {
        setProduct(p);
        if (p) {
          const rel = await fetchRelatedProducts(handle, p.productType, 4);
          setRelated(rel);
        }
      })
      .finally(() => setLoading(false));
  }, [handle]);

  if (loading) {
    return (
      <div className="container mx-auto py-20 grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-[--teal]" />
      </div>
    );
  }
  if (!product) {
    return (
      <div className="container mx-auto py-20 text-center">
        <p className="text-2xl font-serif mb-4">Product not found</p>
        <Button asChild variant="dark">
          <Link to="/shop">
            <ArrowLeft className="h-4 w-4" /> Back to shop
          </Link>
        </Button>
      </div>
    );
  }

  const variant = product.variants.edges[variantIndex]?.node;
  const images = product.images.edges;
  const compareAt = variant?.compareAtPrice ?? product.compareAtPrice;
  const currentPrice =
    variant?.price.amount ?? product.priceRange.minVariantPrice.amount;
  const discount = calcDiscountPercent(currentPrice, compareAt?.amount);

  const handleAdd = async () => {
    if (!variant) return;
    const wrapped: ShopifyProduct = { node: product };
    await addItem({
      product: wrapped,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity,
      selectedOptions: variant.selectedOptions || [],
    });
    toast.success(`${product.title} added to cart`);
  };

  const handleBuyNow = () => {
    const message = `Hello Trendora Marts, I'd like to order:\n\n${product.title} × ${quantity} — ${formatPrice(parseFloat(currentPrice) * quantity)}\n\nName: \nPhone: \nDelivery Address: \nPayment: Cash on Delivery`;
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: product.description?.slice(0, 100),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Product link copied");
      }
    } catch {
      /* cancelled */
    }
  };

  return (
    <>
      <Helmet>
        <title>{product.title} — Trendora Marts</title>
        <meta
          name="description"
          content={
            product.description?.slice(0, 160) ||
            "Shop premium products at Trendora Marts."
          }
        />
        <meta property="og:title" content={`${product.title} — Trendora Marts`} />
        <meta property="og:image" content={images[0]?.node.url} />
      </Helmet>
      <div className="container mx-auto px-4 py-10">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[--teal] mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Images */}
          <div>
            <div className="relative aspect-square bg-secondary rounded-lg overflow-hidden mb-4 group">
              {images[imageIndex] && (
                <img
                  src={images[imageIndex].node.url}
                  alt={images[imageIndex].node.altText || product.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              )}
              {discount > 0 && (
                <span className="absolute top-4 left-4 bg-[#e11d48] text-white text-sm font-bold px-3 py-1.5 rounded-full shadow">
                  -{discount}% OFF
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImageIndex(i)}
                    className={`aspect-square rounded-md overflow-hidden border-2 ${
                      i === imageIndex ? "border-[--teal]" : "border-transparent"
                    }`}
                  >
                    <img
                      src={img.node.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {product.productType && (
              <p className="text-[--teal] uppercase text-xs tracking-[0.3em] mb-3 font-semibold">
                {product.productType}
              </p>
            )}
            <h1 className="text-3xl sm:text-4xl font-serif mb-3 text-[--navy]">
              {product.title}
            </h1>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex text-[--gold]">
                {"★★★★★".split("").map((s, i) => (
                  <Star key={i} className="h-4 w-4 fill-[--gold] text-[--gold]" />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                (128 verified reviews)
              </span>
            </div>

            <div className="flex items-baseline gap-3 mb-2 flex-wrap">
              <p className="text-3xl font-serif text-[--navy] font-bold">
                {formatPrice(currentPrice)}
              </p>
              {compareAt && (
                <>
                  <p className="text-lg text-muted-foreground line-through">
                    {formatPrice(compareAt.amount)}
                  </p>
                  <span className="bg-[#e11d48] text-white text-xs font-bold px-2 py-1 rounded">
                    -{discount}%
                  </span>
                </>
              )}
            </div>
            <p className="text-sm text-[--teal] font-medium mb-6 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> In Stock — Ready to Ship
            </p>

            {product.description && (
              <p className="text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">
                {product.description}
              </p>
            )}

            {product.variants.edges.length > 1 && (
              <div className="mb-6">
                <p className="text-sm font-medium mb-2">Options</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.edges.map((v, i) => (
                    <button
                      key={v.node.id}
                      onClick={() => setVariantIndex(i)}
                      className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                        i === variantIndex
                          ? "border-[--teal] bg-[--teal]/10"
                          : "border-border hover:border-[--teal]/50"
                      }`}
                    >
                      {v.node.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-6">
              <p className="text-sm font-medium mb-2">Quantity</p>
              <div className="inline-flex items-center border border-border rounded-md">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="px-3 py-2 hover:bg-secondary"
                  aria-label="Decrease"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-5 min-w-[3rem] text-center font-medium">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="px-3 py-2 hover:bg-secondary"
                  aria-label="Increase"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                onClick={handleAdd}
                variant="dark"
                size="lg"
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Add to Cart"
                )}
              </Button>
              <Button
                onClick={handleBuyNow}
                size="lg"
                className="flex-1 bg-[#25D366] hover:bg-[#1ebe5a] text-white"
              >
                <MessageCircle className="h-4 w-4 mr-1" /> Buy Now on WhatsApp
              </Button>
            </div>

            <div className="flex gap-4 text-sm mb-6">
              <button
                onClick={() => {
                  setWishlisted((w) => !w);
                  toast.success(
                    wishlisted ? "Removed from wishlist" : "Added to wishlist",
                  );
                }}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-[#e11d48]"
              >
                <Heart
                  className={`h-4 w-4 ${wishlisted ? "fill-[#e11d48] text-[#e11d48]" : ""}`}
                />
                {wishlisted ? "Wishlisted" : "Add to Wishlist"}
              </button>
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-[--teal]"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border">
              {[
                { i: Truck, t: "Fast Delivery", d: "Across Pakistan" },
                { i: ShieldCheck, t: "Cash on Delivery", d: "Pay on arrival" },
                { i: RefreshCw, t: "Easy Returns", d: "Within 7 days" },
              ].map((b) => (
                <div key={b.t} className="flex items-start gap-3">
                  <b.i className="h-5 w-5 text-[--teal] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{b.t}</p>
                    <p className="text-xs text-muted-foreground">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features / Specifications */}
        {(product.features?.length || product.specifications?.length) && (
          <div className="grid md:grid-cols-2 gap-10 mt-16">
            {product.features && product.features.length > 0 && (
              <div>
                <h2 className="text-2xl font-serif text-[--navy] mb-4">
                  Key Features
                </h2>
                <ul className="space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-[--teal] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {product.specifications && product.specifications.length > 0 && (
              <div>
                <h2 className="text-2xl font-serif text-[--navy] mb-4">
                  Specifications
                </h2>
                <dl className="border border-border rounded-lg overflow-hidden">
                  {product.specifications.map((s, i) => (
                    <div
                      key={s.name}
                      className={`grid grid-cols-2 gap-3 px-4 py-3 text-sm ${
                        i % 2 === 0 ? "bg-secondary/60" : "bg-background"
                      }`}
                    >
                      <dt className="text-muted-foreground">{s.name}</dt>
                      <dd className="font-medium">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        )}

        {/* Shipping & Returns */}
        <div className="grid md:grid-cols-2 gap-6 mt-16">
          <div className="p-6 rounded-lg bg-secondary border border-border">
            <h3 className="font-serif text-xl text-[--navy] mb-2 flex items-center gap-2">
              <Truck className="h-5 w-5 text-[--teal]" /> Shipping Information
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Fast delivery within 3–5 working days all across Pakistan. Cash on
              Delivery is available on every order. Free shipping on selected
              items.
            </p>
          </div>
          <div className="p-6 rounded-lg bg-secondary border border-border">
            <h3 className="font-serif text-xl text-[--navy] mb-2 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-[--teal]" /> Return Policy
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Easy 7-day return policy for damaged or defective items. Contact
              our support team on WhatsApp for hassle-free assistance.
            </p>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-16">
          <h2 className="text-2xl font-serif text-[--navy] mb-6">
            Customer Reviews
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                n: "Ahmed R.",
                c: "Lahore",
                q: "Excellent quality, exactly as shown in the pictures. Delivery was quick.",
              },
              {
                n: "Sana K.",
                c: "Karachi",
                q: "Very happy with my purchase. Trendora Marts is now my go-to store.",
              },
              {
                n: "Bilal M.",
                c: "Islamabad",
                q: "Genuine product and fair price. Highly recommend to everyone.",
              },
            ].map((r, i) => (
              <div
                key={i}
                className="p-6 rounded-lg bg-card border border-border"
              >
                <div className="flex text-[--gold] mb-2">
                  {"★★★★★".split("").map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-[--gold] text-[--gold]" />
                  ))}
                </div>
                <p className="text-sm italic mb-3">"{r.q}"</p>
                <p className="text-sm font-semibold text-[--navy]">
                  {r.n}
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    — {r.c}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-serif text-[--navy] mb-6">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {related.map((p) => (
                <ProductCard key={p.node.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
